import React, { useRef, useState } from 'react';
import { API, authFetch } from './api';

// Resolve a stored logo_url for <img>: registry-served logos are relative
// ("/api/associations/{id}/logo") and must be prefixed with this app's API
// origin. (Logos are upload-only, so there are no external http URLs anymore,
// but we keep the http passthrough for safety.)
export function logoSrc(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API}${url}`;
}

// Upload a File to an association's logo; returns the stored logo_url.
export async function uploadLogoFile(associationId, file) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const base64 = String(dataUrl).split(',')[1];
  const resp = await authFetch(`${API}/api/associations/${associationId}/logo`, {
    method: 'POST',
    body: JSON.stringify({ content_type: file.type, data_base64: base64 }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.detail || 'Logo upload failed');
  return data.logo_url;
}

export default function LogoUploader({ associationId, currentUrl, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setErr(''); setBusy(true);
    try {
      const url = await uploadLogoFile(associationId, f);
      onUploaded(`${url}?t=${Date.now()}`); // cache-bust so it shows immediately
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {currentUrl && <img src={logoSrc(currentUrl)} alt="logo" className="h-10 w-auto rounded bg-white/5 p-1" />}
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
        onChange={onFile} className="hidden" />
      <button type="button" onClick={() => inputRef.current && inputRef.current.click()} disabled={busy}
        className="px-3 py-1.5 text-sm bg-[#243B55] text-white rounded hover:bg-[#2d4a6b] disabled:opacity-50">
        {busy ? 'Uploading…' : (currentUrl ? 'Replace logo' : 'Upload logo')}
      </button>
      <span className="text-[10px] text-gray-500">PNG, JPG, SVG, WEBP or GIF · under 2 MB</span>
      {err && <span className="text-red-400 text-xs">{err}</span>}
    </div>
  );
}
